// client/src/coms/voip/p2pCameraShare.js
import { RoomEvent } from 'livekit-client';
import { participantMayShare, remoteMayShare } from './sharePermissions';

const stateByRoom = new WeakMap();
let lastBoundRoom = null;

function getState(room) {
  const r = room || null;
  if (!r) return null;

  let s = stateByRoom.get(r);
  if (!s) {
    s = {
      enabled: false,
      sending: false,
      localStream: null,
      localTrack: null,
      maxPeers: 1,
      peers: new Map(),
      remoteStreamsByIdentity: new Map(),
      err: '',
      bound: false,
      quality: 'thumb',
      iceQueueByPeerKey: new Map(),
    };
    stateByRoom.set(r, s);
  }
  return s;
}

function stopTracks(stream) {
  if (!stream) return;
  try {
    for (const t of stream.getTracks ? stream.getTracks() : []) {
      try {
        t.stop();
      } catch {}
    }
  } catch {}
}

function encode(obj) {
  return new TextEncoder().encode(JSON.stringify(obj));
}

function decode(payload) {
  try {
    return JSON.parse(new TextDecoder().decode(payload));
  } catch {
    return null;
  }
}

function notifyUpdated(room) {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pa-p2p-cams-updated', { detail: { ok: true } }));
    }
  } catch {}

  try {
    if (room) {
      if (typeof room.__pa_p2p_cam_rev !== 'number') room.__pa_p2p_cam_rev = 0;
      room.__pa_p2p_cam_rev += 1;
    }
  } catch {}
}

function getLocalIdentity(room) {
  try {
    return String(room?.localParticipant?.identity || '').trim();
  } catch {
    return '';
  }
}

function getLocalSid(room) {
  try {
    return String(room?.localParticipant?.sid || '').trim();
  } catch {
    return '';
  }
}

function makePeerKeyFromParticipant(p) {
  const sid = String(p?.sid || '').trim();
  if (sid) return sid;
  const identity = String(p?.identity || '').trim();
  if (identity) return `id:${identity}`;
  return '';
}

function makePeerKeyFromMsg(msg, participant) {
  // LiveKit's participant is authoritative; self-reported sender fields that disagree are spoofs.
  const verifiedSid = String(participant?.sid || '').trim();
  const verifiedIdentity = String(participant?.identity || '').trim();
  const claimedSid = String(msg?.fromSid || '').trim();
  const claimedIdentity = String(msg?.from || '').trim();
  if (verifiedSid && claimedSid && claimedSid !== verifiedSid) return '';
  if (verifiedIdentity && claimedIdentity && claimedIdentity !== verifiedIdentity) return '';

  const sid = verifiedSid || claimedSid;
  if (sid) return sid;

  const identity = verifiedIdentity || claimedIdentity;
  if (identity) return `id:${identity}`;
  return '';
}

function identityFromPeerKey(peerKey) {
  const k = String(peerKey || '').trim();
  if (!k) return '';
  if (k.startsWith('id:')) return k.slice(3);
  return '';
}

function publishSignal(room, msg, target) {
  if (!room || !room.localParticipant) return;

  const toSid = typeof target === 'object' && target ? String(target.sid || '').trim() : '';
  const toIdentity = typeof target === 'object' && target ? String(target.identity || '').trim() : String(target || '').trim();

  const out = {
    ...msg,
    __pa: 'p2p_cam_v1',
    to: toIdentity || null,
    toSid: toSid || null,
    from: String(room.localParticipant.identity || ''),
    fromSid: String(room.localParticipant.sid || ''),
    ts: Date.now(),
  };

  try {
    const p = room.localParticipant.publishData(encode(out), { reliable: true });
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch {}
}

function normalizeIceServers(val) {
  try {
    if (!val) return null;
    const raw = typeof val === 'string' ? JSON.parse(val) : val;
    if (!raw) return null;

    const arr = Array.isArray(raw) ? raw : raw.iceServers || raw.ice_servers || raw.servers || null;
    if (!Array.isArray(arr) || !arr.length) return null;

    const out = [];
    for (const s of arr) {
      if (!s) continue;
      const urls = s.urls || s.url || null;
      if (!urls) continue;

      const item = { urls };
      if (s.username) item.username = s.username;
      if (s.credential) item.credential = s.credential;
      out.push(item);
    }
    return out.length ? out : null;
  } catch {
    return null;
  }
}

function getConfiguredIceServers() {
  try {
    if (typeof window === 'undefined') return null;
    const w = window;

    const direct =
      normalizeIceServers(w.__VOXEMERA_ICE_SERVERS) ||
      normalizeIceServers(w.__PA_ICE_SERVERS) ||
      normalizeIceServers(w.__ICE_SERVERS);

    if (direct && direct.length) return direct;

    const url = String(w.__VOXEMERA_TURN_URL || w.__TURN_URL || '').trim();
    const username = String(w.__VOXEMERA_TURN_USERNAME || w.__TURN_USERNAME || '').trim();
    const credential = String(w.__VOXEMERA_TURN_CREDENTIAL || w.__TURN_CREDENTIAL || '').trim();

    if (url) {
      const s = { urls: [url] };
      if (username) s.username = username;
      if (credential) s.credential = credential;
      return [s];
    }
  } catch {}
  return null;
}

function makeIceServers() {
  const cfg = getConfiguredIceServers();
  if (cfg && cfg.length) return cfg;
  return [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }];
}

function trackEnded(track, onEnded) {
  if (!track) return;
  try {
    track.onended = () => {
      try {
        onEnded && onEnded();
      } catch {}
    };
  } catch {}
  try {
    track.addEventListener?.('ended', () => {
      try {
        onEnded && onEnded();
      } catch {}
    });
  } catch {}
}

function getPeer(state, peerKey) {
  const k = String(peerKey || '').trim();
  if (!k) return null;
  return state.peers.get(k) || null;
}

function ensurePeerMeta(peer, meta) {
  if (!peer) return null;
  if (!peer.meta || typeof peer.meta !== 'object') peer.meta = {};
  if (meta && typeof meta === 'object') Object.assign(peer.meta, meta);

  if (typeof peer.meta.lastRestartAt !== 'number') peer.meta.lastRestartAt = 0;
  if (typeof peer.meta.restarting !== 'boolean') peer.meta.restarting = false;
  if (typeof peer.meta.statsTimer !== 'number' && peer.meta.statsTimer !== null) peer.meta.statsTimer = null;
  if (typeof peer.meta.lastAppliedAt !== 'number') peer.meta.lastAppliedAt = 0;
  if (typeof peer.meta.lastIsRelay !== 'boolean' && peer.meta.lastIsRelay !== null) peer.meta.lastIsRelay = null;
  if (typeof peer.meta.closing !== 'boolean') peer.meta.closing = false;

  return peer.meta;
}

function setPeer(state, peerKey, pc, role, meta) {
  const k = String(peerKey || '').trim();
  if (!k) return;
  state.peers.set(k, { pc, role: role || 'unknown', meta: meta || null });
  ensurePeerMeta(state.peers.get(k));
}

function stopPeerMonitor(peer) {
  const p = peer || null;
  if (!p) return;
  const m = ensurePeerMeta(p);
  if (!m) return;

  if (m.statsTimer) {
    try {
      clearInterval(m.statsTimer);
    } catch {}
    m.statsTimer = null;
  }
}

function closePeer(state, peerKey) {
  const k = String(peerKey || '').trim();
  if (!k) return;

  const p = state.peers.get(k);
  if (!p) return;

  try {
    const m = ensurePeerMeta(p);
    if (m) m.closing = true;
  } catch {}

  try {
    stopPeerMonitor(p);
  } catch {}

  try {
    p.pc && (p.pc.onicecandidate = null);
  } catch {}
  try {
    p.pc && (p.pc.ontrack = null);
  } catch {}
  try {
    p.pc && (p.pc.onconnectionstatechange = null);
  } catch {}
  try {
    p.pc && (p.pc.oniceconnectionstatechange = null);
  } catch {}
  try {
    p.pc && (p.pc.onicegatheringstatechange = null);
  } catch {}
  try {
    p.pc && p.pc.close();
  } catch {}

  try {
    state.iceQueueByPeerKey.delete(k);
  } catch {}

  state.peers.delete(k);
}

function dropRemoteStreamByPeerKey(room, peerKey) {
  const s = getState(room);
  if (!s) return;

  const k = String(peerKey || '').trim();
  if (!k) return;

  const peer = s.peers.get(k);
  const identity = String(peer?.meta?.identity || '').trim() || identityFromPeerKey(k);
  if (!identity) return;

  try {
    const existing = s.remoteStreamsByIdentity.get(identity);
    if (existing) stopTracks(existing);
  } catch {}

  s.remoteStreamsByIdentity.delete(identity);
}

function stopReceivingFrom(room, peerKey) {
  const s = getState(room);
  if (!s) return;

  const k = String(peerKey || '').trim();
  if (!k) return;

  try {
    dropRemoteStreamByPeerKey(room, k);
  } catch {}

  closePeer(s, k);

  try {
    room.__pa_p2p_cams = getP2PCameraRemoteStreams(room);
  } catch {}
  notifyUpdated(room);
}

function normalizeQuality(q) {
  const v = String(q || 'thumb').trim();
  if (['thumb', 'expanded', '720', '1080', '1440', '2160'].includes(v)) return v;
  return 'thumb';
}

function getProfiles(quality) {
  const q = normalizeQuality(quality);

  if (q === 'expanded') {
    return {
      capture: { w: 1280, h: 720, fps: 20 },
      sender: { minBitrate: 200000, maxBitrate: 1000000, maxFramerate: 20, degradationPreference: 'maintain-resolution' },
    };
  }

  if (q === '720') {
    return {
      capture: { w: 1280, h: 720, fps: 20 },
      sender: { minBitrate: 300000, maxBitrate: 1600000, maxFramerate: 20, degradationPreference: 'maintain-resolution' },
    };
  }

  if (q === '1080') {
    return {
      capture: { w: 1920, h: 1080, fps: 20 },
      sender: { minBitrate: 400000, maxBitrate: 2500000, maxFramerate: 20, degradationPreference: 'maintain-resolution' },
    };
  }

  if (q === '1440') {
    return {
      capture: { w: 2560, h: 1440, fps: 20 },
      sender: { minBitrate: 500000, maxBitrate: 4000000, maxFramerate: 20, degradationPreference: 'maintain-resolution' },
    };
  }

  if (q === '2160') {
    return {
      capture: { w: 3840, h: 2160, fps: 20 },
      sender: { minBitrate: 800000, maxBitrate: 6000000, maxFramerate: 20, degradationPreference: 'maintain-resolution' },
    };
  }

  return {
    capture: { w: 640, h: 360, fps: 20 },
    sender: { minBitrate: 150000, maxBitrate: 700000, maxFramerate: 20, degradationPreference: 'maintain-framerate' },
  };
}

function trySetSenderParams(sender, profile) {
  const s = sender || null;
  const p = profile || null;
  if (!s || !p) return;

  try {
    const params = s.getParameters() || {};
    if (!Array.isArray(params.encodings) || !params.encodings.length) params.encodings = [{}];
    if (!params.encodings[0]) params.encodings[0] = {};

    if (typeof p.minBitrate === 'number') params.encodings[0].minBitrate = Math.floor(p.minBitrate);
    if (typeof p.maxBitrate === 'number') params.encodings[0].maxBitrate = Math.floor(p.maxBitrate);
    if (typeof p.maxFramerate === 'number') params.encodings[0].maxFramerate = Math.floor(p.maxFramerate);
    if (p.degradationPreference) params.degradationPreference = String(p.degradationPreference);

    try {
      params.encodings[0].priority = 'high';
      params.encodings[0].networkPriority = 'high';
    } catch {}

    s.setParameters(params).catch(() => {});
  } catch {}
}

async function readSelectedCandidatePairIsRelay(pc) {
  const p = pc || null;
  if (!p || typeof p.getStats !== 'function') return null;

  let stats = null;
  try {
    stats = await p.getStats(null);
  } catch {
    return null;
  }

  if (!stats) return null;

  const byId = new Map();
  try {
    stats.forEach((r) => {
      if (r && r.id) byId.set(r.id, r);
    });
  } catch {}

  let pair = null;

  try {
    for (const r of byId.values()) {
      if (!r) continue;
      if (r.type === 'transport' && r.selectedCandidatePairId) {
        const cp = byId.get(r.selectedCandidatePairId);
        if (cp && cp.type === 'candidate-pair') {
          pair = cp;
          break;
        }
      }
    }
  } catch {}

  if (!pair) {
    try {
      for (const r of byId.values()) {
        if (!r) continue;
        if (r.type !== 'candidate-pair') continue;
        if (r.selected === true) {
          pair = r;
          break;
        }
      }
    } catch {}
  }

  if (!pair) return null;

  const localId = pair.localCandidateId || null;
  const remoteId = pair.remoteCandidateId || null;

  const lc = localId ? byId.get(localId) : null;
  const rc = remoteId ? byId.get(remoteId) : null;

  const lcType = lc && lc.candidateType ? String(lc.candidateType) : '';
  const rcType = rc && rc.candidateType ? String(rc.candidateType) : '';

  if (lcType === 'relay' || rcType === 'relay') return true;
  return false;
}

function applyNetworkAdaptiveSenderParams(room, peerKey, isRelay) {
  const s = getState(room);
  if (!s) return;

  const peer = getPeer(s, peerKey);
  if (!peer || peer.role !== 'sender' || !peer.pc) return;

  let sender = null;
  try {
    const senders = peer.pc.getSenders ? peer.pc.getSenders() : [];
    sender = Array.isArray(senders) ? senders.find((x) => x && x.track && x.track.kind === 'video') : null;
  } catch {}

  const base = getProfiles(s.quality).sender;
  if (!sender || !base) return;

  if (!isRelay) {
    trySetSenderParams(sender, base);
    return;
  }

  const relayProfile = {
    ...base,
    maxBitrate: Math.min(Number(base.maxBitrate) || 800000, 800000),
    maxFramerate: Math.min(Number(base.maxFramerate) || 20, 20),
  };

  trySetSenderParams(sender, relayProfile);
}

async function pollPeerNetAndApply(room, peerKey) {
  const s = getState(room);
  if (!s) return;

  const peer = getPeer(s, peerKey);
  if (!peer || !peer.pc) return;

  const m = ensurePeerMeta(peer);
  if (!m || m.closing) return;

  const now = Date.now();
  if (now - (m.lastAppliedAt || 0) < 500) return;

  let isRelay = null;
  try {
    isRelay = await readSelectedCandidatePairIsRelay(peer.pc);
  } catch {
    isRelay = null;
  }

  if (typeof isRelay !== 'boolean') return;
  if (m.lastIsRelay === isRelay) return;

  m.lastIsRelay = isRelay;
  m.lastAppliedAt = now;

  try {
    applyNetworkAdaptiveSenderParams(room, peerKey, isRelay);
  } catch {}
}

function pickRemotePeerByKey(room, peerKey) {
  const k = String(peerKey || '').trim();
  if (!k) return null;

  let found = null;
  try {
    room?.remoteParticipants?.forEach?.((p) => {
      if (found) return;
      const key = makePeerKeyFromParticipant(p);
      if (key === k) {
        found = {
          key,
          sid: String(p?.sid || '').trim(),
          identity: String(p?.identity || '').trim(),
        };
      }
    });
  } catch {}

  if (found) return found;

  const identity = identityFromPeerKey(k);
  return {
    key: k,
    sid: k.startsWith('id:') ? '' : k,
    identity,
  };
}

async function restartPeerAsSender(room, peerKey) {
  const s = getState(room);
  if (!s) return;

  const peer = getPeer(s, peerKey);
  if (!peer || peer.role !== 'sender') return;

  const m = ensurePeerMeta(peer);
  if (!m || m.closing) return;

  const now = Date.now();
  if (m.restarting) return;
  if (now - (m.lastRestartAt || 0) < 4000) return;

  m.restarting = true;
  m.lastRestartAt = now;

  try {
    closePeer(s, peerKey);
  } catch {}

  try {
    if (s.localTrack && s.localStream && s.sending) {
      const target = pickRemotePeerByKey(room, peerKey);
      if (target && target.key) {
        await connectSenderTo(room, target, s.localTrack);
      }
    }
  } catch {}

  m.restarting = false;
}

function bindPeerMonitor(room, peerKey) {
  const s = getState(room);
  if (!s) return;

  const peer = getPeer(s, peerKey);
  if (!peer || !peer.pc) return;

  const m = ensurePeerMeta(peer);
  if (!m) return;

  try {
    stopPeerMonitor(peer);
  } catch {}

  const pc = peer.pc;

  pc.onconnectionstatechange = () => {
    try {
      pollPeerNetAndApply(room, peerKey);
    } catch {}

    try {
      const st = String(pc.connectionState || '');
      if ((st === 'failed' || st === 'disconnected') && peer.role === 'sender') {
        restartPeerAsSender(room, peerKey);
      }
      if (st === 'failed' && peer.role === 'receiver') {
        stopReceivingFrom(room, peerKey);
      }
    } catch {}
  };

  pc.oniceconnectionstatechange = () => {
    try {
      pollPeerNetAndApply(room, peerKey);
    } catch {}

    try {
      const st = String(pc.iceConnectionState || '');
      if ((st === 'failed' || st === 'disconnected') && peer.role === 'sender') {
        restartPeerAsSender(room, peerKey);
      }
      if (st === 'failed' && peer.role === 'receiver') {
        stopReceivingFrom(room, peerKey);
      }
    } catch {}
  };

  pc.onicegatheringstatechange = () => {
    try {
      pollPeerNetAndApply(room, peerKey);
    } catch {}
  };

  try {
    m.statsTimer = setInterval(() => {
      try {
        pollPeerNetAndApply(room, peerKey);
      } catch {}
    }, 2000);
  } catch {
    m.statsTimer = null;
  }

  try {
    pollPeerNetAndApply(room, peerKey);
  } catch {}
}

async function captureCamera(quality) {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('camera_unsupported');
  }

  const prof = getProfiles(quality);
  const want = prof.capture;

  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      width: { ideal: want.w },
      height: { ideal: want.h },
      frameRate: { ideal: want.fps, max: want.fps },
      facingMode: 'user',
    },
    audio: false,
  });

  const track = stream?.getVideoTracks?.()[0];
  if (!track) {
    stopTracks(stream);
    throw new Error('camera_track_missing');
  }

  try {
    track.contentHint = 'motion';
  } catch {}

  return { stream, track };
}

function pickRemotePeers(room) {
  const out = [];
  try {
    room?.remoteParticipants?.forEach?.((p) => {
      const sid = String(p?.sid || '').trim();
      const identity = String(p?.identity || '').trim();
      const key = makePeerKeyFromParticipant(p);
      if (key) out.push({ key, sid, identity });
    });
  } catch {}
  return out;
}

function queueIce(state, peerKey, candidate) {
  const k = String(peerKey || '').trim();
  if (!k || !candidate) return;

  try {
    if (!state.iceQueueByPeerKey.has(k)) state.iceQueueByPeerKey.set(k, []);
    const q = state.iceQueueByPeerKey.get(k) || [];
    q.push(candidate);
    state.iceQueueByPeerKey.set(k, q);
  } catch {}
}

async function flushIce(state, peerKey) {
  const k = String(peerKey || '').trim();
  if (!k) return;

  const peer = getPeer(state, k);
  if (!peer?.pc) return;

  let queued = [];
  try {
    queued = state.iceQueueByPeerKey.get(k) || [];
  } catch {
    queued = [];
  }

  if (!queued.length) return;

  try {
    const pc = peer.pc;
    const rd = pc.remoteDescription || null;
    if (!rd || !rd.type) return;
  } catch {}

  try {
    for (const cand of queued) {
      try {
        await peer.pc.addIceCandidate(cand);
      } catch {}
    }
  } catch {}

  try {
    state.iceQueueByPeerKey.delete(k);
  } catch {}
}

async function connectSenderTo(room, targetPeer, localTrack) {
  const s = getState(room);
  if (!s) return;

  const target = targetPeer && typeof targetPeer === 'object' ? targetPeer : { key: String(targetPeer || '').trim(), sid: '', identity: '' };
  const peerKey = String(target.key || '').trim();
  if (!peerKey) return;

  closePeer(s, peerKey);

  const pc = new RTCPeerConnection({ iceServers: makeIceServers() });
  setPeer(s, peerKey, pc, 'sender', { sid: String(target.sid || '').trim(), identity: String(target.identity || '').trim() });

  pc.onicecandidate = (ev) => {
    if (ev?.candidate) publishSignal(room, { type: 'ice', candidate: ev.candidate }, { sid: target.sid, identity: target.identity });
  };

  let sender = null;
  try {
    sender = pc.addTrack(localTrack, s.localStream);
  } catch {}

  try {
    const profile = getProfiles(s.quality).sender;
    if (sender && profile) trySetSenderParams(sender, profile);
  } catch {}

  try {
    bindPeerMonitor(room, peerKey);
  } catch {}

  try {
    const offer = await pc.createOffer({ offerToReceiveVideo: false, offerToReceiveAudio: false });
    await pc.setLocalDescription(offer);
    publishSignal(room, { type: 'offer', sdp: pc.localDescription }, { sid: target.sid, identity: target.identity });
  } catch {
    closePeer(s, peerKey);
  }
}

async function acceptOffer(room, fromPeerKey, msg, participant) {
  const s = getState(room);
  if (!s) return;

  const fromKey = String(fromPeerKey || '').trim();
  if (!fromKey) return;

  stopReceivingFrom(room, fromKey);

  const fromSid = String(participant?.sid || msg?.fromSid || '').trim();
  const fromIdentity = String(participant?.identity || msg?.from || '').trim();

  const pc = new RTCPeerConnection({ iceServers: makeIceServers() });
  setPeer(s, fromKey, pc, 'receiver', { sid: fromSid, identity: fromIdentity });

  const target = { sid: fromSid, identity: fromIdentity };

  pc.onicecandidate = (ev) => {
    if (ev?.candidate) publishSignal(room, { type: 'ice', candidate: ev.candidate }, target);
  };

  pc.ontrack = (ev) => {
    const stream = ev.streams?.[0];
    if (!stream) return;

    const id = String(fromIdentity || '').trim() || identityFromPeerKey(fromKey);
    if (!id) return;

    s.remoteStreamsByIdentity.set(id, stream);
    room.__pa_p2p_cams = getP2PCameraRemoteStreams(room);
    notifyUpdated(room);
  };

  try {
    bindPeerMonitor(room, fromKey);
  } catch {}

  try {
    await pc.setRemoteDescription(msg.sdp);

    try {
      await flushIce(s, fromKey);
    } catch {}

    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    publishSignal(room, { type: 'answer', sdp: pc.localDescription }, target);
  } catch {
    stopReceivingFrom(room, fromKey);
  }
}

async function acceptAnswer(room, fromPeerKey, msg) {
  const s = getState(room);
  if (!s) return;

  const fromKey = String(fromPeerKey || '').trim();
  if (!fromKey) return;

  const peer = getPeer(s, fromKey);
  if (!peer?.pc) return;

  try {
    await peer.pc.setRemoteDescription(msg.sdp);
  } catch {}

  try {
    await flushIce(s, fromKey);
  } catch {}

  try {
    bindPeerMonitor(room, fromKey);
  } catch {}
}

async function acceptIce(room, fromPeerKey, msg) {
  const s = getState(room);
  if (!s) return;

  const fromKey = String(fromPeerKey || '').trim();
  if (!fromKey) return;

  const cand = msg?.candidate || null;
  if (!cand) return;

  const peer = getPeer(s, fromKey);
  if (!peer?.pc) {
    queueIce(s, fromKey, cand);
    return;
  }

  try {
    const rd = peer.pc.remoteDescription || null;
    if (!rd || !rd.type) {
      queueIce(s, fromKey, cand);
      return;
    }
  } catch {
    queueIce(s, fromKey, cand);
    return;
  }

  try {
    await peer.pc.addIceCandidate(cand);
  } catch {}
}

function shouldAcceptMessageForMe(room, msg) {
  const toSid = String(msg?.toSid || '').trim();
  const to = msg?.to == null ? '' : String(msg.to).trim();

  const mySid = getLocalSid(room);
  const myIdentity = getLocalIdentity(room);

  if (!toSid && !to) return true;
  if (toSid && mySid && toSid === mySid) return true;
  if (to && myIdentity && to === myIdentity) return true;

  return false;
}

function ensureBound(room) {
  if (!room) return;

  lastBoundRoom = room;
  const s = getState(room);
  if (!s || s.bound) return;

  s.bound = true;

  const onData = async (payload, participant) => {
    const msg = decode(payload);
    if (!msg || msg.__pa !== 'p2p_cam_v1') return;

    if (!shouldAcceptMessageForMe(room, msg)) return;

    const fromKey = makePeerKeyFromMsg(msg, participant);
    if (!fromKey) return;

    if (msg.type === 'offer') {
      // The server cannot police direct media: refuse senders whose role / timeout forbids the camera.
      if (!remoteMayShare(room, participant && participant.identity, 'camera')) return undefined;
      return acceptOffer(room, fromKey, msg, participant);
    }
    if (msg.type === 'answer') return acceptAnswer(room, fromKey, msg);
    if (msg.type === 'ice') return acceptIce(room, fromKey, msg);
    if (msg.type === 'stop') return stopReceivingFrom(room, fromKey);
  };

  room.on?.(RoomEvent.DataReceived, onData);

  room.on?.(RoomEvent.ParticipantConnected, async (p) => {
    if (!s.sending || !s.localTrack) return;

    const peer = { key: makePeerKeyFromParticipant(p), sid: String(p?.sid || '').trim(), identity: String(p?.identity || '').trim() };
    if (!peer.key) return;

    const connected = Array.from(s.peers.values()).filter((x) => x?.role === 'sender').length;
    if (connected >= s.maxPeers) return;

    await connectSenderTo(room, peer, s.localTrack);
  });

  room.on?.(RoomEvent.ParticipantDisconnected, (p) => {
    const key = makePeerKeyFromParticipant(p);
    if (key) stopReceivingFrom(room, key);
  });

  // Camera right revoked mid-share: stop our own camera / drop theirs (direct streams bypass the SFU).
  room.on?.(RoomEvent.ParticipantPermissionsChanged, (_prev, p) => {
    if (!p) return;
    if (p === room.localParticipant) {
      if (s.sending && !participantMayShare(p.permissions, 'camera')) stopP2PCameraShare(room).catch(() => {});
      return;
    }
    if (!participantMayShare(p.permissions, 'camera')) {
      const key = makePeerKeyFromParticipant(p);
      if (key) stopReceivingFrom(room, key);
    }
  });
}

export function initP2PCameraShare(room) {
  if (!room) return;
  ensureBound(room);
  room.__pa_p2p_cams = getP2PCameraRemoteStreams(room);
  if (!room.__pa_p2p_local_camera_stream) room.__pa_p2p_local_camera_stream = null;
  notifyUpdated(room);
}

export async function startP2PCameraShare(room, { maxPeers = 1 } = {}) {
  if (!room?.localParticipant) return { ok: false, reason: 'no_room' };

  ensureBound(room);
  const s = getState(room);
  if (!s) return { ok: false, reason: 'no_state' };

  s.err = '';
  s.maxPeers = Math.max(0, Number(maxPeers) || 0);

  try {
    await stopP2PCameraShare(room);
  } catch {}

  try {
    const { stream, track } = await captureCamera(s.quality);

    s.localStream = stream;
    s.localTrack = track;
    s.enabled = true;
    s.sending = true;

    room.__pa_p2p_local_camera_stream = stream;

    trackEnded(track, () => {
      stopP2PCameraShare(room);
    });

    const remotes = pickRemotePeers(room).slice(0, s.maxPeers);
    for (const peer of remotes) {
      await connectSenderTo(room, peer, track);
    }

    room.__pa_p2p_cams = getP2PCameraRemoteStreams(room);
    notifyUpdated(room);

    return { ok: true, peers: remotes.length };
  } catch (e) {
    s.err = String(e?.message || e || 'p2p_cam_failed');
    stopTracks(s.localStream);
    s.localStream = null;
    s.localTrack = null;
    s.enabled = false;
    s.sending = false;
    room.__pa_p2p_local_camera_stream = null;
    notifyUpdated(room);
    return { ok: false, reason: s.err };
  }
}

export async function stopP2PCameraShare(room) {
  if (!room) return { ok: true };

  ensureBound(room);
  const s = getState(room);
  if (!s) return { ok: true };

  s.err = '';

  for (const peerKey of Array.from(s.peers.keys())) {
    const peer = s.peers.get(peerKey);
    const sid = String(peer?.meta?.sid || '').trim();
    const identity = String(peer?.meta?.identity || '').trim() || identityFromPeerKey(peerKey);
    try {
      publishSignal(room, { type: 'stop' }, { sid, identity });
    } catch {}
    closePeer(s, peerKey);
  }

  stopTracks(s.localStream);
  s.localStream = null;
  s.localTrack = null;

  for (const id of Array.from(s.remoteStreamsByIdentity.keys())) {
    stopTracks(s.remoteStreamsByIdentity.get(id));
  }

  s.remoteStreamsByIdentity.clear();

  room.__pa_p2p_cams = {};
  room.__pa_p2p_local_camera_stream = null;

  notifyUpdated(room);

  s.enabled = false;
  s.sending = false;

  return { ok: true };
}

export function getP2PCameraEnabled(room) {
  return !!getState(room)?.enabled;
}

export function getP2PCameraErr(room) {
  return String(getState(room)?.err || '');
}

export function getP2PCameraRemoteStreams(room) {
  const s = getState(room);
  const out = {};
  if (!s) return out;

  s.remoteStreamsByIdentity.forEach((stream, id) => {
    if (stream) out[String(id)] = stream;
  });

  return out;
}

export async function setP2PCameraQuality(room, quality) {
  const s = getState(room);
  if (!s) return { ok: false, reason: 'no_state' };

  s.quality = normalizeQuality(quality);

  try {
    s.peers.forEach((peer) => {
      if (!peer || peer.role !== 'sender' || !peer.pc) return;
      const senders = peer.pc.getSenders ? peer.pc.getSenders() : [];
      const sender = Array.isArray(senders) ? senders.find((x) => x && x.track && x.track.kind === 'video') : null;
      if (sender) trySetSenderParams(sender, getProfiles(s.quality).sender);
    });
  } catch {}

  return { ok: true };
}